import { Alert, VFlow } from 'bold-ui'
import { Breadcrumb } from 'components/breadcrumb'
import { PageContent } from 'components/layout/PageContent'
import { PageHeaderSection } from 'components/PageHeaderSection'
import { PivotTable, PivotTableInitialValues } from 'components/pivot-table/PivotTable'
import { useTreeBuilder } from 'components/pivot-table/tree-builder/useTreeBuilder'
import { AsyncProcessingNameEnum, TipoAgrupamentoRelatorioGerencialEnum, TipoModuloEnum } from 'graphql/types.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { useCallback, useEffect, useState } from 'react'
import { isUndefinedOrNull } from 'util/checks'

import {
  ASYNC_LOADING_MESSAGE,
  labelAgrupamentoMap,
  RELATORIO_GERENCIAL_CADASTRO_INDIVIDUAL_FILTER_STORAGE_KEY,
  RELATORIO_GERENCIAL_CADASTRO_INDIVIDUAL_NAME,
  RELATORIOS_GERENCIAIS_ANALYTICS_SELECIONOU_RELATORIO_EVENTO,
} from '../common/model-relatorioGerencial'
import { useAsyncRelatorioGerencial } from '../common/useAsyncRelatorioGerencial'
import { RelatorioGerencialFiltrosFormModel } from '../components/form/model-relatorioGerencialFiltrosForm'
import { RelatorioGerencialTableWrapper } from '../components/RelatorioGerencialTableWrapper'
import { cadastroIndividualKeyMapping } from './keyMapping-relatorioGerencialCadastroIndividual'
import { RelatorioGerencialCadastroIndividualModel } from './model-relatorioGerencialCadastroIndividual'

const OPERATIONS = {
  build: '/api/gerenciais/cadastro-individual/build',
  meta: '/api/gerenciais/cadastro-individual/meta',
}

export function RelatorioGerencialCadastroIndividualView() {
  const [pivotTableInitialValues, setPivotTableInitialValues] = useState<
    PivotTableInitialValues<RelatorioGerencialCadastroIndividualModel>
  >()

  const { analytics } = useFirebase()

  const {
    meta,
    metaState,
    treeBuilderStatus: { isBuilding, isSearchingMeta },
    isMetaEmpty,
    setTipoAgrupamento,
    setIsSearchingMeta,
    tipoAgrupamento,
    ...rest
  } = useTreeBuilder<RelatorioGerencialCadastroIndividualModel>({
    keyMapping: cadastroIndividualKeyMapping,
    operations: OPERATIONS,
  })

  const onGetMeta = useCallback(
    ({ tipoAgrupamento }: RelatorioGerencialFiltrosFormModel) => {
      setTipoAgrupamento(tipoAgrupamento)
    },
    [setTipoAgrupamento]
  )

  const { handleSubmit, filtros, isAsyncProcessingRunning } = useAsyncRelatorioGerencial({
    asyncProcessingName: AsyncProcessingNameEnum.RELATORIO_GERENCIAL_CADASTRO_INDIVIDUAL,
    filterStorageKey: RELATORIO_GERENCIAL_CADASTRO_INDIVIDUAL_FILTER_STORAGE_KEY,
    isSearchingMeta,
    hasMetaState: !isUndefinedOrNull(metaState),
    meta,
    onGetMeta,
    setIsSearchingMeta,
  })

  const groupByLabel = tipoAgrupamento ? labelAgrupamentoMap[tipoAgrupamento] : undefined

  useEffect(() => {
    analytics.logEvent(RELATORIOS_GERENCIAIS_ANALYTICS_SELECIONOU_RELATORIO_EVENTO, {
      relatorio: RELATORIO_GERENCIAL_CADASTRO_INDIVIDUAL_NAME,
    })
  }, [analytics])

  return (
    <>
      <Breadcrumb title={RELATORIO_GERENCIAL_CADASTRO_INDIVIDUAL_NAME} />

      <PageHeaderSection title={RELATORIO_GERENCIAL_CADASTRO_INDIVIDUAL_NAME} />

      <PageContent type='filled'>
        <VFlow>
          <Alert inline type='info'>
            Este relatório tem por objetivo apresentar os dados de cadastro individual de acordo com os filtros
            selecionados. A contagem padrão é por pessoa (cidadão único). A origem dos dados são as fichas de cadastro
            individual do CDS e PEC.
          </Alert>
          <RelatorioGerencialTableWrapper
            isEmpty={isMetaEmpty}
            onSubmit={handleSubmit}
            modulo={TipoModuloEnum.RELATORIO_GERENCIAL_CADASTRO_INDIVIDUAL}
            keyMapping={cadastroIndividualKeyMapping}
            loading={isSearchingMeta || isAsyncProcessingRunning}
            loadingMessage={ASYNC_LOADING_MESSAGE}
            filtrosLocalStorage={filtros}
            setPivotTableInitialValues={setPivotTableInitialValues}
            hasFiltroTipoAgrupamento
          >
            {metaState && (
              <PivotTable<RelatorioGerencialCadastroIndividualModel>
                title={RELATORIO_GERENCIAL_CADASTRO_INDIVIDUAL_NAME}
                treeMeta={metaState}
                keyMapping={cadastroIndividualKeyMapping}
                isBuilding={isBuilding}
                initialValues={pivotTableInitialValues}
                groupByLabel={groupByLabel}
                enableSearchField
                showTotals={tipoAgrupamento === TipoAgrupamentoRelatorioGerencialEnum.PESSOAS}
                {...rest}
              />
            )}
          </RelatorioGerencialTableWrapper>
        </VFlow>
      </PageContent>
    </>
  )
}
